import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, MenuItem, Typography, Checkbox } from '@mui/material';
import { InputField } from '../../../components/InputController';
import { useFormik } from 'formik';
import CustomDropdown from '../../../components/customDropdown';
import {
  ApplicationQuestionAndAnswerDto,
  CustomQuestionTypeEnum,
  CustomQuestionResponseTypeEnum,
  ApplicationQuestionResponseV2Dto,
  UpsertApplicantEmploymentHistoryV2Dto,
  EmploymentStatusEnum,
  UpdateAddressDTO,
} from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { CustomDropZone } from '../../../pmui/onboarding/components/CustomDropZone';
import { uploadDocuments, selectDocumentState, deleteDocument } from '../../../shared/sagas/document/documentSlice';
import { applicationStyles } from './CustomStyles';
import { InosioDateFormat } from '../../../shared/util/formaters';
import { convertSqlDateToUSCalendarDate, convertUSCalendarDateToSqlDate } from '../../../shared/util/converters';
import { approvedISOStandards } from '../../../shared/util/approvedISOStandards';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { applicantDocumentMetadata, getEmploymentIncomeRecords, selectApplicantsState, updateCustomQuestionsAnswersV2, updateEmploymentIncomeRecord } from '../sagas/applicants/applicantsSlice';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import CustomQuestionsSection from './CustomQuestionsSection';
import * as Yup from 'yup';
import { DobRegex, E164PhoneRegex, EmailRegex } from '../../../utils/regex';
import { PhoneNumberInput } from '../../../shared/PhoneNumberInput';

const schemaValidationApplicationEmployment = Yup.object({
  Line1: Yup.string()
    .nullable()
    .test({
      name: 'Line1 Check',
      exclusive: true,
      message: 'Please enter a street address',
      test: (value, testContext) => {
        if (testContext.parent.Status === 'Not Employed') return true;
        const statusFlag = testContext.parent.Status === 'Employed' || testContext.parent.Status === 'Self Employed';

        return statusFlag && !!testContext.parent.Line1;
      },
    }),
  Line2: Yup.string().nullable(),
  City: Yup.string()
    .nullable()
    .test({
      name: 'City Check',
      exclusive: true,
      message: 'Please enter a city',
      test: (value, testContext) => {
        if (testContext.parent.Status === 'Not Employed') return true;
        const statusFlag = testContext.parent.Status === 'Employed' || testContext.parent.Status === 'Self Employed';

        return statusFlag && !!testContext.parent.City;
      },
    }),
  State: Yup.string()
    .nullable()
    .test({
      name: 'State Check',
      exclusive: true,
      message: 'Please select a state/province',
      test: (value, testContext) => {
        if (testContext.parent.Status === 'Not Employed') return true;
        const statusFlag = testContext.parent.Status === 'Employed' || testContext.parent.Status === 'Self Employed';

        return statusFlag && !!testContext.parent.State;
      },
    }),
  Zip: Yup.string()
    .min(5, 'Please enter a five digit zip')
    .max(5, 'Please enter a five digit zip')
    .nullable()
    .test({
      name: 'Zip Check',
      exclusive: true,
      message: 'Please enter a five digit zip',
      test: (value, testContext) => {
        if (testContext.parent.Status === 'Not Employed') return true;
        const statusFlag = testContext.parent.Status === 'Employed' || testContext.parent.Status === 'Self Employed';

        return statusFlag && !!testContext.parent.Zip;
      },
    }),
  Country: Yup.string()
    .nullable()
    .test({
      name: 'Country Check',
      exclusive: true,
      message: 'Please select a valid country',
      test: (value, testContext) => {
        if (testContext.parent.Status === 'Not Employed') return true;
        const statusFlag = testContext.parent.Status === 'Employed' || testContext.parent.Status === 'Self Employed';

        return statusFlag && !!testContext.parent.Country;
      },
    }),
  Status: Yup.string().required('Required Status'),
  Industry: Yup.string().nullable(),
  Title: Yup.string().nullable(),
  CompanyName: Yup.string().nullable(),
  CompanyPhone: Yup.string().matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Phone Number]').required('Required Phone Number'),
  MonthlySalary: Yup.string()
    .nullable()
    .matches(/^[1-9][0-9]*$/, 'Monthly salary cannot be 0 or start with 0'), // Ensure it doesn't start with 0
  StartDate: Yup.string()
    .min(10, 'Please enter a date in this format MM/DD/YYYY')
    .max(10, 'Please enter a date in this format MM/DD/YYYY')
    .matches(DobRegex, 'Please enter a valid date')
    .nullable()
    .test({
      name: 'Start Date Check with End Date',
      exclusive: true,
      message: 'Start Date should be less than End Date',
      test: (value, testContext) => {
        const returnObj = testContext.parent.StartDate && testContext.parent.EndDate && new Date(testContext.parent.StartDate) > new Date(testContext.parent.EndDate);
        return !returnObj;
      },
    }),
  CurrentlyEmployed: Yup.boolean(),
  EndDate: Yup.string().when('CurrentlyEmployed', {
    is: false,
    then: Yup.string()
      .min(10, 'Please enter a date in this format MM/DD/YYYY')
      .max(10, 'Please enter a date in this format MM/DD/YYYY')
      .matches(DobRegex, 'Please enter a valid date')
      .required('Required format MM/DD/YYYY')
      .test({
        name: 'End Date Check',
        exclusive: true,
        message: 'End Date should be less than today',
        test: (value, testContext) => {
          const returnObj = testContext.parent.EndDate && new Date(testContext.parent.EndDate) > new Date();
          return !returnObj;
        },
      })
      .test({
        name: 'End Date Check with Start Date',
        exclusive: true,
        message: 'End Date should be greater than Start Date',
        test: (value, testContext) => {
          const returnObj = testContext.parent.StartDate && testContext.parent.EndDate && new Date(testContext.parent.StartDate) > new Date(testContext.parent.EndDate);
          return !returnObj;
        },
      }),
    otherwise: Yup.string().nullable(),
  }),
  CompanyContactName: Yup.string().nullable(),
  CompanyContactPhone: Yup.string().matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Subscriber Number]').required('Required Phone Number'),
  CompanyContactEmail: Yup.string().matches(EmailRegex, 'Please type a valid Email.').nullable(),
  OtherIncomeAmount: Yup.string().required(),
  OtherIncomeDescription: Yup.string().nullable(),
});

type schemaValidationApplicationEmploymentType = Yup.InferType<typeof schemaValidationApplicationEmployment>;
const incomeFileLimit = 10;

export default function ApplicationEmployment() {
  const query = useQuery();
  const { id: employmentUuidParam } = useParams<{ id: string }>();
  const hasValidationQueryParam = !!query.get('validation');
  const classes = applicationStyles();
  const dispatch = useDispatch();

  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;

  //selectors
  const { ipaUser } = useSelector(selectAuthState);
  const { isUploadingDocuments, isUploadingDocumentsSuccess, isUploadingDocumentsFailed } = useSelector(selectDocumentState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);
  const {
    Application,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    CustomQuestionAnswers,
    isFetchingEmploymentIncomeRecordsSuccess,
    isFetchingEmploymentIncomeRecordsFailed,
    EmploymentIncomeRecords,
    fetchDocumentResponse,
    isFetchingDocumentsSuccess,
    isFetchingDocumentsFailed,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isUpdatingEmploymentIncomeRecordFailed,
    isUpdatingEmploymentIncomeRecordSuccess,
    isUpdatingEmploymentIncomeRecordError,
  } = useSelector(selectApplicantsState);

  //local state
  const [localError, setLocalError] = useState<string | null>(null);
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [incomeFiles, setIncomeFiles] = useState<Array<any>>([]);
  const [currentEmploymentUuid] = useState(employmentUuidParam);
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<Array<ApplicationQuestionAndAnswerDto>>([]);
  const bottomRef = useRef<HTMLElement>(null);
  const [initialFormValues, setInitialFormValues] = useState<schemaValidationApplicationEmploymentType>({
    Line1: '',
    Line2: '',
    City: '',
    State: '',
    Zip: '',
    Country: 'US',
    Status: EmploymentStatusEnum.Employed,
    Industry: '',
    Title: '',
    CompanyName: '',
    CompanyPhone: '',
    MonthlySalary: '',
    StartDate: '',
    EndDate: '',
    CurrentlyEmployed: false,
    CompanyContactName: '',
    CompanyContactPhone: '',
    CompanyContactEmail: '',
    OtherIncomeAmount: '',
    OtherIncomeDescription: '',
  });

  //#region  updated useffects v2

  //fetch employment/income records
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid && !isFetchingEmploymentIncomeRecordsSuccess) {
      //they must have got here via direct link or via refresh.
      dispatch(getEmploymentIncomeRecords(ipaUser?.currentProfile.uuid));
    } else if (ipaUser?.currentProfile.uuid && isFetchingEmploymentIncomeRecordsSuccess) {
      const thisEmploymentRecord = EmploymentIncomeRecords?.find((e) => e.Uuid === currentEmploymentUuid);
      if (thisEmploymentRecord) {
        // setCurrentEmploymentUuid();
        setInitialFormValues({
          ...initialFormValues,
          Line1: thisEmploymentRecord.Address?.Line1 ?? '',
          Line2: thisEmploymentRecord.Address?.Line2 ?? '',
          City: thisEmploymentRecord.Address?.City ?? '',
          State: thisEmploymentRecord.Address?.State ?? initialFormValues.State,
          Zip: thisEmploymentRecord.Address?.Zip ?? '',
          Country: thisEmploymentRecord.Address?.Country || 'US',
          Status: thisEmploymentRecord.Status ?? EmploymentStatusEnum.Employed,
          Industry: thisEmploymentRecord.Industry ?? '',
          Title: thisEmploymentRecord.Title ?? '',
          CompanyName: thisEmploymentRecord.CompanyName ?? '',
          CompanyPhone: thisEmploymentRecord.CompanyPhone ?? '',
          MonthlySalary: thisEmploymentRecord.MonthlySalary ? `${thisEmploymentRecord.MonthlySalary}` : '',
          StartDate: thisEmploymentRecord.StartDate ? convertSqlDateToUSCalendarDate(thisEmploymentRecord.StartDate) : '',
          EndDate: thisEmploymentRecord.EndDate ? convertSqlDateToUSCalendarDate(thisEmploymentRecord.EndDate) : '',
          CurrentlyEmployed: thisEmploymentRecord.CurrentlyEmployed,
          CompanyContactName: thisEmploymentRecord.CompanyContactName ?? '',
          CompanyContactPhone: thisEmploymentRecord.CompanyContactPhone ?? '',
          CompanyContactEmail: thisEmploymentRecord.CompanyContactEmail ?? '',
          OtherIncomeAmount: !!thisEmploymentRecord.OtherIncomeAmount || thisEmploymentRecord.OtherIncomeAmount === 0 ? `${thisEmploymentRecord.OtherIncomeAmount}` : '', //0 is allowed when not employed
          OtherIncomeDescription: thisEmploymentRecord.OtherIncomeDescription ?? '',
        });
      } else {
        //bad
      }
    }
  }, [ipaUser, currentEmploymentUuid, isFetchingEmploymentIncomeRecordsSuccess]);

  //populate custom questions once prop group details received
  useEffect(() => {
    if (propertyGroupDetails?.Uuid && isFetchingCustomQuestionAnswersSuccess) {
      const organizationEmployeeCustomQuestions = (propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsQuestions ?? []).filter(
        (q) => q.CustomQuestionType === CustomQuestionTypeEnum.EmployeeV1,
      );
      setQuestionsAndAnswers(
        organizationEmployeeCustomQuestions.map((q) => {
          const previouslySavedResponseToThisQuestion = CustomQuestionAnswers?.find((a) => a.QuestionUuid === q.Uuid && a.BelongsToUuid === currentEmploymentUuid);

          const question: ApplicationQuestionAndAnswerDto = {
            ...q,
            ApplicantUuid: previouslySavedResponseToThisQuestion?.ApplicantUuid!,
            BelongsToEntityUuid: currentEmploymentUuid,
          };

          switch (question.CustomQuestionResponseType) {
            case CustomQuestionResponseTypeEnum.FreeText:
              question.TextQuestionAnswer = previouslySavedResponseToThisQuestion?.Response?.toString();
              break;
            case CustomQuestionResponseTypeEnum.Boolean:
              question.BooleanQuestionAnswer = previouslySavedResponseToThisQuestion?.Response === undefined ? undefined : Boolean(previouslySavedResponseToThisQuestion?.Response);
              break;
            default:
              break;
          }

          return question;
        }),
      );
    }
  }, [propertyGroupDetails, isFetchingCustomQuestionAnswersSuccess]);

  //deal with the documents once received
  useEffect(() => {
    if (isFetchingDocumentsSuccess) {
      const filesForThisSpecificRecord = fetchDocumentResponse
        ?.filter((d) => d.documentTypeId === 4 && d.belongsToEntityUuid === currentEmploymentUuid)
        .map((d) => {
          const file: applicantDocumentMetadata = {
            name: d.fileName, //is just this good enough?
            remoteUuid: d.remoteUuid,
          };
          return file;
        });
      setIncomeFiles(filesForThisSpecificRecord);
    }
  }, [isFetchingDocumentsSuccess, fetchDocumentResponse]);
  //#endregion

  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingEmploymentIncomeRecordsSuccess && isFetchingApplicationSuccess && isFetchingCustomQuestionAnswersSuccess && isFetchingDocumentsSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingEmploymentIncomeRecordsFailed || isFetchingApplicationFailure || isFetchingCustomQuestionAnswersFailure || isFetchingDocumentsFailed) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [
    isFetchingPropertyGroupDetailsSuccess,
    isFetchingPropertyGroupDetailsFailed,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    isFetchingDocumentsSuccess,
    isFetchingDocumentsFailed,
    isFetchingEmploymentIncomeRecordsSuccess,
    isFetchingEmploymentIncomeRecordsFailed,
  ]);

  //show/hide error if document upload failed
  useEffect(() => {
    if (isUploadingDocumentsFailed) {
      setLocalError('Error uploading document(s).');
    }
  }, [isUploadingDocumentsSuccess, isUploadingDocumentsFailed]);

  useEffect(() => {
    if (isUpdatingEmploymentIncomeRecordFailed) {
      setLocalError(isUpdatingEmploymentIncomeRecordError.message ?? 'Error saving information');
    }

    if (isUpdatingEmploymentIncomeRecordSuccess) {
      setLocalError(null);
    }
  }, [isUpdatingEmploymentIncomeRecordSuccess, isUpdatingEmploymentIncomeRecordFailed]);

  //#region  v2 new helper functions

  const handleSaveCustomQuestionAnswer = (e: any, questionUuid: string) => {
    const answeredQuestion = questionsAndAnswers.find((q) => q.Uuid === questionUuid);

    if (answeredQuestion) {
      dispatch({
        type: updateCustomQuestionsAnswersV2.type,
        payload: {
          applicationUuid: Application.Uuid,
          upsertDto: {
            QuestionUuid: answeredQuestion?.Uuid,
            ResponseType: answeredQuestion?.CustomQuestionResponseType,
            Response: answeredQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean ? e.target.value === 'true' : e.target.value,
            BelongsToUuid: currentEmploymentUuid,
            ApplicantUuid: ipaUser?.currentProfile.uuid,
          } as ApplicationQuestionResponseV2Dto,
        },
      });
    }
  };

  const handleCustomQuestionFieldChanged = (e: any, questionUuid: string) => {
    setQuestionsAndAnswers(
      questionsAndAnswers.map((qa) => {
        if (qa.Uuid === questionUuid) {
          //found our question.

          if (qa.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
            return {
              ...qa,
              BooleanQuestionAnswer: e.target.value === 'true',
            };
          } else
            return {
              ...qa,
              TextQuestionAnswer: e.target.value,
            };
        }
        return qa;
      }),
    );
  };

  const handleFormValuesChanged = (e: any) => {
    /*
     */

    const addressPart = {
      City: !formik.errors.City ? formik.values.City || undefined : undefined,
      State: !formik.errors.State ? formik.values.State || undefined : undefined,
      Zip: !formik.errors.Zip ? formik.values.Zip || undefined : undefined,
      Line1: !formik.errors.Line1 ? formik.values.Line1 || undefined : undefined,
      Line2: !formik.errors.Line2 ? formik.values.Line2 || undefined : undefined,
      Country: !formik.errors.Country ? formik.values.Country || undefined : undefined,
    } as UpdateAddressDTO;

    const payload = {
      Status: !formik.errors.Status ? (formik.values.Status ? (formik.values.Status as EmploymentStatusEnum) : undefined) : undefined, //TODO fix this to be the real value instead of just "employed"
      Title: !formik.errors.Title ? formik.values.Title || undefined : undefined,
      Industry: !formik.errors.Industry ? formik.values.Industry || undefined : undefined,
      CompanyName: !formik.errors.CompanyName ? formik.values.CompanyName || undefined : undefined,
      CompanyPhone: !formik.errors.CompanyPhone ? formik.values.CompanyPhone || undefined : undefined,
      MonthlySalary: !formik.errors.MonthlySalary ? parseInt(formik.values.MonthlySalary || '0') || undefined : undefined,
      CompanyContactName: !formik.errors.CompanyContactName ? formik.values.CompanyContactName || undefined : undefined,
      CompanyContactPhone: !formik.errors.CompanyContactPhone ? formik.values.CompanyContactPhone : undefined,
      CompanyContactEmail: !formik.errors.CompanyContactEmail ? formik.values.CompanyContactEmail || undefined : undefined,
      StartDate: !formik.errors.StartDate ? (formik.values.StartDate ? convertUSCalendarDateToSqlDate(formik.values.StartDate) : undefined) : undefined,
      EndDate: !formik.errors.EndDate ? (formik.values.EndDate ? convertUSCalendarDateToSqlDate(formik.values.EndDate) : null) : undefined,
      CurrentlyEmployed: !formik.errors.CurrentlyEmployed ? formik.values.CurrentlyEmployed : undefined,
      OtherIncomeAmount: !formik.errors.OtherIncomeAmount ? parseInt(formik.values.OtherIncomeAmount ?? '') ?? undefined : undefined,
      OtherIncomeDescription: !formik.errors.OtherIncomeDescription ? formik.values.OtherIncomeDescription || undefined : undefined,
      Address: addressPart,
    } as UpsertApplicantEmploymentHistoryV2Dto;

    for (const [key] of Object.entries(payload)) {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    }

    dispatch({
      type: updateEmploymentIncomeRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        employmentUuid: currentEmploymentUuid,
        updateDto: payload,
      },
    });
  };

  //#endregion

  const formik = useFormik<schemaValidationApplicationEmploymentType>({
    initialValues: initialFormValues,
    validationSchema: schemaValidationApplicationEmployment,
    onSubmit: async (values) => {},
    enableReinitialize: true,
    validateOnMount: true,
  });

  // useEffect(()=>{
  //   if(hasValidationQueryParam)
  //     formik.validateForm();
  // },[hasValidationQueryParam]);

  const handleIncomeFilesAdded = (filesToUpload: File[]) => {
    let files = incomeFiles;

    if (files.length + filesToUpload.length <= incomeFileLimit) {
      filesToUpload.forEach((af: File) => {
        if (!files.find((f) => f.name === af.name)) {
          files = [...files, af];
        }
      });
      setIncomeFiles(files);

      const uploadingFiles = files?.filter((f) => f.name && !f.remoteUuid);
      if (uploadingFiles.length > 0) {
        dispatch({
          type: uploadDocuments.type,
          payload: {
            belongsToEntityUuid: currentEmploymentUuid,
            documents: uploadingFiles,
            documentType: 'ProofOfIncome',
          },
        });
      }
    }
  };

  const handleDeleteIncomeFile = (file: any) => {
    if (file.remoteUuid) {
      dispatch(deleteDocument(file.remoteUuid));
    } else {
      //there is no remoteUuid, so it must not have even uploaded. Just remove it from local state.
      const newList = incomeFiles.filter((f) => f.name !== file.name);
      setIncomeFiles(newList);
    }
  };

  //#region formatters
  const resetStateSelection = () => {
    formik.setFieldValue('State', '');
  };

  const handleStartDate = (StartDate: string) => {
    const formattedStartDate = InosioDateFormat(StartDate);
    formik.setFieldValue('StartDate', formattedStartDate);
  };

  const handleEndDate = (EndDate: string) => {
    const formattedEndDate = InosioDateFormat(EndDate);
    formik.setFieldValue('EndDate', formattedEndDate);
  };

  //#endregion formatters

  return (
    <Box sx={{ width: '100%', p: 4 }}>
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Grid container spacing={4}>
          {localError && (
            <Grid item xs={12}>
              <Typography className={classes.globalText}>{localError}</Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Typography sx={{ mb: 2 }} className={classes.labelText}>
              Employment & Income
            </Typography>
            <CustomDropdown
              id="employmentStatusDropdown"
              isFullWidth
              label={'Status'}
              value={formik.values?.Status ?? ''}
              handleChange={(e) => {
                formik.setFieldValue('Status', e.target.value);
              }}
              menuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '24px',
                  },
                },
              }}
              showAsterisk={true}
              handleBlur={(e) => {
                formik.handleBlur(e);
                handleFormValuesChanged(e);
              }}
            >
              <MenuItem style={{ height: '36px' }} key={EmploymentStatusEnum.Employed} value={EmploymentStatusEnum.Employed}>
                Employed
              </MenuItem>
              <MenuItem style={{ height: '36px' }} key={EmploymentStatusEnum.NotEmployed} value={EmploymentStatusEnum.NotEmployed}>
                Not Employed
              </MenuItem>
              <MenuItem style={{ height: '36px' }} key={EmploymentStatusEnum.SelfEmployed} value={EmploymentStatusEnum.SelfEmployed}>
                Self Employed
              </MenuItem>
            </CustomDropdown>

            {formik.values?.Status !== EmploymentStatusEnum.NotEmployed && (
              <Fragment>
                {formik.values?.Status === EmploymentStatusEnum.Employed && (
                  <InputField
                    label={'Job Title'}
                    isError={!formik.values?.Title && hasValidationQueryParam}
                    errors={formik.errors?.Title}
                    touched={formik.touched?.Title}
                    {...formik.getFieldProps('Title')}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                    showAsterisk={!formik.values.Title}
                  />
                )}
                {formik.values?.Status === EmploymentStatusEnum.SelfEmployed && (
                  <InputField
                    label={'Industry'}
                    isError={!formik.values?.Industry && hasValidationQueryParam}
                    errors={formik.errors?.Industry}
                    touched={formik.touched?.Industry}
                    {...formik.getFieldProps('Industry')}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                    showAsterisk={!formik.values.Industry}
                  />
                )}
                <InputField
                  label={'Company Name'}
                  isError={!formik.values?.CompanyName && hasValidationQueryParam}
                  errors={formik.errors?.CompanyName}
                  touched={formik.touched?.CompanyName}
                  {...formik.getFieldProps('CompanyName')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                  showAsterisk={!formik.values.CompanyName}
                />
                {formik.values?.Status === EmploymentStatusEnum.Employed && (
                  <Box>
                    <PhoneNumberInput
                      {...formik.getFieldProps('CompanyPhone')}
                      label="Company Phone"
                      showErrorMessage={!!formik.errors?.CompanyPhone && !!formik.touched?.CompanyPhone}
                      onChange={(phone) => {
                        formik.setFieldValue('CompanyPhone', phone.startsWith('+') ? phone : `+${phone}`);
                      }}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        handleFormValuesChanged(e);
                      }}
                      showErrorPinkBackground={!!formik.errors?.CompanyPhone && hasValidationQueryParam}
                      errorMessage={formik.errors?.CompanyPhone}
                      showAsterisk={true}
                    />
                    {/*Adding margins to phoneinput above is making it wierd so adding it here, do not remove */}
                    <Box sx={{ marginTop: '16px' }}></Box>
                  </Box>
                )}
                <InputField
                  label={'Monthly Salary'}
                  isError={!formik.values?.MonthlySalary && hasValidationQueryParam}
                  errors={formik.errors?.MonthlySalary}
                  touched={formik.touched?.MonthlySalary}
                  showAsterisk={!formik.values.MonthlySalary}
                  {...formik.getFieldProps('MonthlySalary')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                  // isError={formik.values?.MonthlySalary?.length === 0 && hasValidationQueryParam}
                  onChange={(e) => {
                    const input = e.target.value;
                    const newValue = input.replace(/[^0-9]/g, ''); // Remove non-numeric characters
                    formik.setFieldValue('MonthlySalary', newValue);
                  }}
                />
                <Grid container>
                  <Grid item xs={6}>
                    <InputField
                      label={'Start Date'}
                      placeholder="MM/DD/YYYY"
                      isError={!formik.values?.StartDate && hasValidationQueryParam}
                      errors={formik.errors?.StartDate}
                      touched={formik.touched?.StartDate}
                      {...formik.getFieldProps('StartDate')}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        handleFormValuesChanged(e);
                      }}
                      onChange={(e) => handleStartDate(e.target.value)}
                      showAsterisk={!formik.values.StartDate}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={6}>
                    <InputField
                      disabled={formik.values.CurrentlyEmployed}
                      label={'End Date'}
                      placeholder="MM/DD/YYYY"
                      // isError={!formik.values.EndDate && !formik.values.CurrentlyEmployed && hasValidationQueryParam}
                      isError={!!formik.errors?.EndDate && hasValidationQueryParam}
                      errors={formik.errors?.EndDate}
                      touched={formik.touched?.EndDate}
                      {...formik.getFieldProps('EndDate')}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        handleFormValuesChanged(e);
                      }}
                      onChange={(e) => {
                        handleEndDate(e.target.value);
                        // if (e.target.value && !!formik.values.CurrentlyEmployed) formik.setFieldValue('CurrentlyEmployed', false);
                      }}
                      showAsterisk={!formik.values.EndDate}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        name={'CurrentlyEmployed'}
                        inputProps={{ 'aria-label': 'controlled' }}
                        checked={formik.values.CurrentlyEmployed}
                        onBlur={(e) => {
                          formik.handleBlur(e);
                          handleFormValuesChanged(e);
                        }}
                        onChange={(e) => {
                          formik.setFieldValue('EndDate', '');
                          formik.handleChange(e); // Update formik state
                        }}
                        sx={{ ml: 1 }}
                      />
                      <Typography className={classes.bodyText}>Currently employed</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Fragment>
            )}

            {formik.values?.Status === EmploymentStatusEnum.NotEmployed && (
              <Fragment>
                <InputField
                  label={'Other Income Avg Monthly Amount'}
                  isError={!!formik.errors?.OtherIncomeAmount && hasValidationQueryParam}
                  errors={formik.errors?.OtherIncomeAmount}
                  touched={formik.touched?.OtherIncomeAmount}
                  showAsterisk={!formik.values.OtherIncomeAmount}
                  {...formik.getFieldProps('OtherIncomeAmount')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                  onChange={(e) => {
                    const input = e.target.value;
                    const newValue = input.replace(/[^0-9]/g, ''); // Remove non-numeric characters
                    formik.setFieldValue('OtherIncomeAmount', newValue);
                  }}
                />
                <InputField
                  label={'Describe your other income'}
                  placeholder="(e.g. Social Security, Pension, Investments)"
                  multiline
                  rows={6}
                  isError={!!formik.errors?.OtherIncomeDescription && hasValidationQueryParam}
                  errors={formik.errors?.OtherIncomeDescription}
                  touched={formik.touched?.OtherIncomeDescription}
                  showAsterisk={false}
                  {...formik.getFieldProps('OtherIncomeDescription')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                />
              </Fragment>
            )}

            {formik.values?.Status === EmploymentStatusEnum.Employed || formik.values?.Status === EmploymentStatusEnum.SelfEmployed ? (
              <>
                <Typography sx={{ mb: 2 }} className={classes.labelText}>
                  Physical Address:
                </Typography>
                <InputField
                  label={'Address Line 1'}
                  isError={!formik.values?.Line1 && hasValidationQueryParam}
                  errors={formik.errors?.Line1}
                  touched={formik.touched?.Line1}
                  {...formik.getFieldProps('Line1')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                  showAsterisk={!formik.values.Line1}
                />
                <InputField
                  label={'Address Line 2'}
                  isError={!!formik.errors?.Line2 && hasValidationQueryParam}
                  errors={formik.errors?.Line2}
                  touched={formik.touched?.Line2}
                  {...formik.getFieldProps('Line2')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                />
                <Grid container direction="row" spacing={1}>
                  <Grid item xs={12} id="city-input">
                    <InputField
                      label={'City'}
                      isError={!formik.values?.City && hasValidationQueryParam}
                      errors={formik.errors?.City}
                      touched={formik.touched?.City}
                      {...formik.getFieldProps('City')}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        handleFormValuesChanged(e);
                      }}
                      showAsterisk={!formik.values.City}
                    />
                  </Grid>
                  <Grid item xs={12} id="state-input">
                    {formik.values?.Country && ['US', 'CA'].includes(formik.values?.Country) ? (
                      <>
                        <CustomDropdown
                          id={'state-dropdown'}
                          isFullWidth
                          label={'State / Province'}
                          name="State"
                          labelId={''}
                          value={formik.values?.State || ''}
                          error={hasValidationQueryParam ? formik.errors?.State : undefined}
                          handleChange={(e) => {
                            formik.setFieldValue('State', e.target.value);
                            formik.handleBlur(e);
                          }}
                          menuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: '24px',
                              },
                            },
                          }}
                          showAsterisk={true}
                          handleBlur={(e) => {
                            formik.handleBlur(e);
                            handleFormValuesChanged(e);
                          }}
                        >
                          {formik.values?.Country === 'US'
                            ? approvedStates.map((stateRecord) => (
                                <MenuItem style={{ height: '36px' }} key={stateRecord.Code} value={stateRecord.Code}>
                                  {stateRecord.State + ' - ' + stateRecord.Code}
                                </MenuItem>
                              ))
                            : approvedProvinces.map((provinceRecord) => (
                                <MenuItem style={{ height: '36px' }} key={provinceRecord.Code} value={provinceRecord.Code}>
                                  {provinceRecord.Province + ' - ' + provinceRecord.Code}
                                </MenuItem>
                              ))}
                        </CustomDropdown>
                        {/* Below standard select is just fake and only for autofill. Ramsey would kill me. */}
                        <select id={'state-dropdown-alt'} name="State" value={formik.values?.State || ''} onChange={(e) => formik.setFieldValue('State', e.target.value)} style={{ display: 'none' }}>
                          {formik.values?.Country === 'US'
                            ? approvedStates.map((stateRecord) => (
                                <option key={stateRecord.Code} value={stateRecord.Code}>
                                  {stateRecord.State + ' - ' + stateRecord.Code}
                                </option>
                              ))
                            : approvedProvinces.map((provinceRecord) => (
                                <option key={provinceRecord.Code} value={provinceRecord.Code}>
                                  {provinceRecord.Province + ' - ' + provinceRecord.Code}
                                </option>
                              ))}
                        </select>
                      </>
                    ) : (
                      <InputField
                        label={'State / Province'}
                        isError={!!formik.errors?.State && hasValidationQueryParam}
                        errors={formik.errors?.State}
                        touched={formik.touched?.State}
                        showAsterisk={!formik.values.State}
                        {...formik.getFieldProps('State')}
                        onBlur={(e) => {
                          formik.handleBlur(e);
                          handleFormValuesChanged(e);
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid container direction="row" spacing={1}>
                  <Grid item xs={12} sm={6} id="zip-input">
                    <InputField
                      label={'Postal Code'}
                      isError={!!formik.errors?.Zip && hasValidationQueryParam}
                      errors={formik.errors?.Zip}
                      touched={formik.touched?.Zip}
                      showAsterisk={!formik.values.Zip}
                      {...formik.getFieldProps('Zip')}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        handleFormValuesChanged(e);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} id="countries-dropdown">
                    <CustomDropdown
                      label={'Country'}
                      isFullWidth
                      handleChange={(e: any) => {
                        resetStateSelection();
                        formik.setFieldValue('Country', e.target.value);
                      }}
                      value={formik.values?.Country || ''}
                      menuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: '24px',
                          },
                        },
                      }}
                      showAsterisk={true}
                      handleBlur={handleFormValuesChanged}
                    >
                      <MenuItem style={{ height: '36px' }} value="">
                        {`Select Country`}
                      </MenuItem>
                      {approvedCountries.map((countryRecord) => (
                        <MenuItem style={{ height: '36px' }} key={countryRecord.Code} value={countryRecord.Code}>
                          {countryRecord.Country + ' - ' + countryRecord.Code}
                        </MenuItem>
                      ))}
                    </CustomDropdown>
                  </Grid>
                </Grid>
              </>
            ) : (
              <></>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            {formik.values?.Status === EmploymentStatusEnum.Employed && (
              <Fragment>
                <Typography sx={{ mb: 2 }} className={classes.labelText}>
                  Verification:
                </Typography>
                <InputField
                  label={'Supervisor/HR Contact Name'}
                  isError={!formik.values?.CompanyContactName && hasValidationQueryParam}
                  errors={formik.errors?.CompanyContactName}
                  touched={formik.touched?.CompanyContactName}
                  showAsterisk={!formik.values.CompanyContactName}
                  {...formik.getFieldProps('CompanyContactName')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                />
                <Box>
                  <PhoneNumberInput
                    {...formik.getFieldProps('CompanyContactPhone')}
                    label="Supervisor/HR Phone"
                    showErrorMessage={!!formik.errors?.CompanyContactPhone && !!formik.touched?.CompanyContactPhone}
                    onChange={(phone) => {
                      formik.setFieldValue('CompanyContactPhone', phone.startsWith('+') ? phone : `+${phone}`);
                    }}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                    showErrorPinkBackground={!!formik.errors?.CompanyContactPhone && hasValidationQueryParam}
                    errorMessage={formik.errors?.CompanyContactPhone}
                    showAsterisk={true}
                  />
                  {/*Adding margins to phoneinput above is making it wierd so adding it here, do not remove */}
                  <Box sx={{ marginTop: '16px' }}></Box>
                </Box>
                <InputField
                  label={'Email Address'}
                  isError={!formik.values?.CompanyContactEmail && hasValidationQueryParam}
                  errors={formik.errors?.CompanyContactEmail}
                  touched={formik.touched?.CompanyContactEmail}
                  showAsterisk={!formik.values.CompanyContactEmail}
                  {...formik.getFieldProps('CompanyContactEmail')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                />
              </Fragment>
            )}

            {!!currentEmploymentUuid && (
              <>
                <Typography sx={{ my: 2 }} className={classes.labelText}>
                  Upload proof of income:
                </Typography>
                <Typography sx={{ ml: 1, mb: 2 }} className={classes.bodyText}>
                  {formik.values?.Status === EmploymentStatusEnum.Employed
                    ? 'Please provide proof of income in the form of 2~3 paystubs or similar financial evidence.'
                    : 'Upload proof of income. e.g W2, paystubs, annual tax forms, bank account statments showing regular deposits, etc.'}
                </Typography>
                {/* maxFileSize must be in MB, example: maxFileSize={20} */}
                <CustomDropZone
                  maxFileSize={20}
                  files={incomeFiles}
                  fileLimit={incomeFileLimit}
                  handleFilesAdded={handleIncomeFilesAdded}
                  handleFileDeleted={handleDeleteIncomeFile}
                  description={`Up to 20MB per file. Up to ${incomeFileLimit} Files at a time.`}
                  height="250px"
                  fileTypes={['image/png', 'image/jpeg', 'application/pdf']} //.txt
                  validationError={
                    hasValidationQueryParam &&
                    !incomeFiles?.length &&
                    propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsRequireEmploymentDocsUpload &&
                    !(formik.values?.Status === EmploymentStatusEnum.NotEmployed)
                  } // User is in validation mode and they are employed/self employed but haven't given any Income files
                  startProgress={isUploadingDocuments}
                  stopProgress={isUploadingDocumentsSuccess}
                  syncProgress={true}
                />
                <Typography style={{ fontFamily: 'Work Sans', fontSize: '14px', marginTop:'10px' }}>{'Supported File Types: .jpg, .pdf, .png'}</Typography>
              </>
            )}
          </Grid>

          <CustomQuestionsSection
            questionsAndAnswers={questionsAndAnswers}
            hasValidationQueryParam={hasValidationQueryParam}
            onFieldChange={handleCustomQuestionFieldChanged}
            onFieldUpdate={handleSaveCustomQuestionAnswer}
            bottomRef={bottomRef}
          />
        </Grid>
      )}
    </Box>
  );
}
